import { create } from "zustand";
import {
	createJSONStorage,
	persist,
	type StateStorage,
} from "zustand/middleware";
import type { SignInData } from "@/core/schemas/sign-in.schema";

// Tracks how far the user has legitimately progressed through the multi-step
// sign-in flow. Each page advances this before navigating to the next step;
// each downstream page verifies it on mount and bounces back to /sig if the
// user tried to jump ahead (e.g. by typing /sig/mfa directly).
export type SignInStep =
	| "credentials"
	| "confirmation"
	| "mfa"
	| "pw-reset"
	| "verification";

interface SignInState {
	signInData: Partial<SignInData>;
	totpSetupUri: string | null;
	isAuthenticated: boolean;
	step: SignInStep | null;
	login: (email: string, rememberMe: boolean) => void;
	logout: () => void;
	resetSignInData: () => void;
	setStep: (step: SignInStep | null) => void;
	setTotpSetupUri: (uri: string | null) => void;
	updateSignInData: (data: Partial<SignInData>) => void;
}

const getStorage = (): StateStorage => {
	if (typeof window !== "undefined") {
		// Check localStorage for a flag or default to sessionStorage
		const isRemembered = localStorage.getItem("rememberMe") === "true";
		return isRemembered ? localStorage : sessionStorage;
	}
	return sessionStorage; // Default to sessionStorage if not in browser context
};

export const useSignInStore = create<SignInState>()(
	persist(
		(set) => ({
			signInData: {},
			totpSetupUri: null,
			isAuthenticated: false,
			step: null,
			login: (email, rememberMe) => {
				// Set a flag in localStorage immediately upon login decision
				if (rememberMe) {
					localStorage.setItem("rememberMe", "true");
				} else {
					localStorage.removeItem("rememberMe");
				}
				set({ signInData: { email, rememberMe }, isAuthenticated: true });
			},
			logout: () => {
				// Clear both storages on logout
				localStorage.removeItem("SignInStore");
				sessionStorage.removeItem("SignInStore");
				// localStorage.removeItem("rememberMe");
				set({ signInData: {} });
			},
			resetSignInData: () =>
				set({ signInData: {}, totpSetupUri: null, step: null }),
			setStep: (step) => set({ step }),
			setTotpSetupUri: (uri) => set({ totpSetupUri: uri }),
			updateSignInData: (newData) =>
				set((state) => ({
					signInData: { ...state.signInData, ...newData },
				})),
		}),
		{
			name: "SignInStore", // unique name for the storage key
			storage: createJSONStorage(getStorage), // Use the dynamic storage getter
		},
	),
);
